/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { ApiCodeBlock } from 'ui/molecules/ApiCodeBlock/ApiCodeBlock';

import styles from './ApiTerminal.module.scss';

import type { ApiTerminalProps } from './ApiTerminal.types';

export const ApiTerminal = ({ steps, autoplayMs = 6200, className }: ApiTerminalProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useIntersection(ref, { threshold: 0.3, continuous: true });

  const [activeIndex, setActiveIndex] = useState(0);
  const [userTook, setUserTook] = useState(false);

  useEffect(() => {
    if (userTook || !inView || steps.length <= 1) {
      return;
    }

    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % steps.length);
    }, autoplayMs);

    return () => clearInterval(id);
  }, [userTook, inView, steps.length, autoplayMs]);

  const active = steps[activeIndex] ?? steps[0];

  const selectStep = (index: number) => {
    setUserTook(true);
    setActiveIndex(index);
  };

  return (
    <div ref={ref} className={cn(styles.root, className)}>
      <div className={styles.steps} role="tablist">
        {steps.map((step, index) => (
          <button
            key={step.key}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            className={cn(styles.step, { [styles.on]: index === activeIndex })}
            onClick={() => selectStep(index)}
          >
            {step.label}
          </button>
        ))}
      </div>

      <ApiCodeBlock
        key={active.key}
        className={styles.code}
        filename={active.filename}
        codeTokens={active.codeTokens}
        results={active.results}
        animated={!userTook && inView}
      />
    </div>
  );
};
